<template>
    <div class="vehicle-position-info-window">
        <div v-if="point.machine_id != null" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('Machine') }}:</span>
            <span class="vehicle-position-info-window__value">{{ point.machine_id }}</span>
        </div>
        <div v-if="point.implement_name" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('Implement') }}:</span>
            <span class="vehicle-position-info-window__value">{{ point.implement_name }}</span>
        </div>
        <div v-if="point.timestamp" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('Time') }}:</span>
            <span class="vehicle-position-info-window__value">{{ vLive.formatDateTime(point.timestamp) }}</span>
        </div>
        <div v-if="showOnlineStatus" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('Status') }}:</span>
            <VehicleOnlineStatusBadge :is-online="point.is_online" />
        </div>

        <Divider v-if="hasGpsSpeed" class="my-2" />

        <div v-if="gpsSpeedDisplay" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('GPS speed') }}:</span>
            <span class="vehicle-position-info-window__value">{{ gpsSpeedDisplay }}</span>
        </div>

        <Divider v-if="hasFlagFields" class="my-2" />

        <div v-if="point.autopilot_on != null" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('Autopilot') }}:</span>
            <span
                class="vehicle-position-info-window__value"
                :class="boolClass(point.autopilot_on)"
            >{{ $t(vLive.boolLabel(point.autopilot_on)) }}</span>
        </div>
        <div v-if="point.implement_working != null" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('Implement working') }}:</span>
            <span
                class="vehicle-position-info-window__value"
                :class="boolClass(point.implement_working)"
            >{{ $t(vLive.boolLabel(point.implement_working)) }}</span>
        </div>

        <Divider v-if="hasIsobusTaskId" class="my-2" />

        <div v-if="hasIsobusTaskId" class="vehicle-position-info-window__row">
            <span class="vehicle-position-info-window__label">{{ $t('ISOBUS task') }}:</span>
            <Button
                variant="link"
                size="small"
                class="vehicle-position-info-window__value"
                @click="openIsobusTask(point.isobus_task_id)"
                v-tooltip.bottom="$t('Navigate to the task')"
            >
                {{ point.isobus_task_id }}
            </Button>
        </div>
        <!-- don't show paths if live on fields map or selected data mode is live -->
        <div v-if="hasIsobusTaskId && showFieldPaths" class="vehicle-position-info-window__paths">
            <div class="vehicle-position-info-window__row">
                <span class="vehicle-position-info-window__label">{{ $t('Field paths') }}:</span>
                <div class="vehicle-position-info-window__value vehicle-position-info-window__value--paths">
                    <ProgressSpinner v-if="vLive.pathsLoading" style="width: 20px; height: 20px;" class="mr-2" />
                    <Select v-else-if="vLive.paths.length > 0"
                        v-model="vLive.selectedPathId"
                        :options="vLive.paths"
                        :optionLabel="(path) => `${path.id} - ${path.name}`"
                        :optionValue="(path) => path.id"
                        :loading="ps.getPlanLoading"
                        size="small"
                        class="dropdown-select"
                        id="path_select"
                        @update:modelValue="vLive.showPath"
                        :placeholder="$t('Select to view on map')"
                    />
                    <span v-else>{{ $t('No paths found for this field') }}</span>
                </div>
            </div>
            <div
                v-if="vLive.selectedPathId && !vLive.pathsLoading && !ps.getPlanLoading"
                class="vehicle-position-info-window__clear-row"
            >
                <Button
                    @click="vLive.hidePath"
                    variant="text"
                    size="small"
                    severity="danger"
                >
                    {{ $t('Hide path') }}
                </Button>
            </div>
        </div>
    </div>
</template>

<script>
import { useVehiclesLiveStore } from '@/stores/vehiclesLive'
import { useImperialUnitsStore } from '@/stores/imperialUnits'
import { useTasksStore } from '@/stores/tasks'
import { useMaps } from '@/stores/maps'
import { useRoute } from 'vue-router'
import VehicleOnlineStatusBadge from '@/components/vehicles/VehicleOnlineStatusBadge.vue'
import { usePlanner } from '@/stores/planner'
import { useAsAppliedMap } from '@/stores/asAppliedMap'

export default {
    name: 'VehiclePositionInfoWindow',
    components: {
        VehicleOnlineStatusBadge,
    },
    props: {
        point: {
            type: Object,
            required: true,
        },
    },
    setup() {
        const vLive = useVehiclesLiveStore()
        const impUnit = useImperialUnitsStore()
        const ts = useTasksStore()
        const ms = useMaps()
        const route = useRoute()
        const ps = usePlanner()
        const aam = useAsAppliedMap()

        return { vLive, impUnit, ts, ms, route, ps, aam }
    },
    async mounted() {
        await this.loadPaths()
    },
    computed: {
        gpsSpeedDisplay() {
            return this.impUnit.formatSpeedMps(this.point.gps_speed_mps)
        },
        hasGpsSpeed() {
            return !!(this.gpsSpeedDisplay)
        },
        hasFlagFields() {
            return this.point.autopilot_on != null
                || this.point.implement_working != null
        },
        hasIsobusTaskId() {
            return (this.point.isobus_task_id != null && this.point.isobus_task_id !== '')
        },
        isFieldsRoute() {
            return this.route.name === 'fields' || this.route.name === 'fields-customer'
        },
        isTaskListRoute() {
            return this.route.name === 'field-tasks'
        },
        showOnlineStatus() {
            return this.point.is_online != null && (this.isFieldsRoute || this.isTaskListRoute)
        },
        showFieldPaths() {
            if (this.isTaskListRoute) return true
            if (this.vLive.liveOnFieldsMap) return false // Live on Fields map view
            if (this.vLive.selectedDataMode === this.vLive.dataModes.LIVE.value) return false
            return true
        },
    },
    methods: {
        boolClass(value) {
            if (value == null) return ''
            return value ? 'vehicle-position-info-window__value--yes' : 'vehicle-position-info-window__value--no'
        },
        async openIsobusTask(isobusTaskId) {
            try {
                const task = await this.ts.ensureTaskLoaded(isobusTaskId)
                if (!task) return

                this.vLive.closePointInfoWindow()
                await this.ms.navToTask(task, {
                    ensurePeriod: 'all',
                    checkAsApplied: true,
                    nexatMachineId: this.point.machine_id,
                })
            } catch (error) {
                console.error('Error opening ISOBUS task:', error)
            }
        },
        async loadPaths() {
            if (!this.showFieldPaths) return // Don't load paths in Live or Live on Fields map view

            this.vLive.pathsLoading = true
            try {
                if (this.vLive.requestedIsobusTaskId !== this.point.isobus_task_id) {
                    this.vLive.requestedIsobusTaskId = this.point.isobus_task_id
                    this.vLive.paths = []

                    // Task List already has tasks loaded — do not call getFieldTask
                    if (this.isTaskListRoute) {
                        const tasks = this.ts.tasksByPeriod[this.ts.activeTasksPeriod]
                            || this.ts.sortedTasks
                        for (const task of tasks) {
                            for (const plan of task.plans || []) {
                                this.vLive.paths.push({
                                    id: plan.id,
                                    name: plan.name,
                                })
                            }
                        }
                        return
                    }

                    const task = await this.ts.getFieldTask(this.point.isobus_task_id)
                    if (!task) return

                    await this.ts.getFieldTasksByPeriod(task.field_id, 'all')

                    for (const fieldTask of this.ts.tasksByPeriod['all']) {
                        for (const plan of fieldTask.plans) {
                            this.vLive.paths.push({
                                id: plan.id,
                                name: plan.name,
                            })
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading paths:', error)
            } finally {
                this.vLive.pathsLoading = false
            }
        },
    },
}
</script>

<style scoped>
.vehicle-position-info-window {
    min-width: 260px;
    max-width: 320px;
    font-size: 13px;
    line-height: 1.45;
    color: #111827;
    margin-top: 20px;
}

.vehicle-position-info-window__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
}

.vehicle-position-info-window__label {
    color: #4b5563;
    font-weight: 600;
    flex: 1 1 auto;
    min-width: 0;
}

.vehicle-position-info-window__value {
    flex: 0 1 auto;
    text-align: right;
    word-break: break-word;
}

.vehicle-position-info-window__value--paths {
    flex: 1 1 auto;
    min-width: 0;
}

.vehicle-position-info-window__clear-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
}

.vehicle-position-info-window__value--yes {
    color: #27ae60;
}

.vehicle-position-info-window__value--no {
    color: #e74c3c;
}

.vehicle-position-info-window :deep(.dropdown-select) {
    --p-select-background: #fff !important;
    --p-select-color: #111827 !important;
    --p-select-border-color: #4b5563 !important;
    width: 100%;
}

.vehicle-position-info-window :deep(.dropdown-select .p-select-label) {
    font-size: 13px;
    line-height: 1.45;
    font-weight: 400;
    text-align: right;
    word-break: break-word;
    padding: 0.15rem 0.35rem;
}
</style>
